import { NativeBridgeResponse } from 'scbk-services/types/bridge/global';
import { isApp } from 'scbk-services/utils/deviceUtils';
import {
    NativeBridgeCallbackFn,
    useNativeBridgeCallbackRegistryStore,
} from '../stores/nativeBridgeCallbackRegistry';
import { v7 } from 'uuid';

/** 브릿지 호출 옵션 (업무 함수에서 사용) */
export interface NativeBridgeCallOptions<T> {
    onlyRequest?: boolean;
    timeoutMillis?: number;
    callback?: {
        once?: boolean;
        fn: NativeBridgeCallbackFn<NativeBridgeResponse<T>>;
    };
}

/**
 * 네이티브 연계 서비스
 */
class NativeBridgeService {
    private static instance: NativeBridgeService | null = null;

    private pendingRequests = new Map<string, Promise<unknown | undefined>>();

    private constructor() {}

    /**
     * 싱글톤 인스턴스 생성 후 리턴
     * @returns
     */
    public static getInstance(): NativeBridgeService {
        if (!this.instance) {
            this.instance = new NativeBridgeService();
        }
        return this.instance;
    }

    /**
     * 임의의 requestId를 생성한다.
     * @returns
     */
    private createRequestId(): string {
        if(crypto?.randomUUID) {
            return crypto.randomUUID()
        }
        
        return v7();
    }

    /**
     * callback 패턴 기반의 네이티브 브릿지 호출 함수
     * @param action
     * @param payload
     * @param options
     * @returns
     */
    public callWithCallback<I extends any = {}, O extends any = {}>(
        action: string,
        payload?: I, // iOS 쿼리 파라미터 구성용
        options?: NativeBridgeCallOptions<O>,
    ) {
        if (!isApp()) {
            new Error('NOT APP');
        }

        const defaultOptions = {
            onlyRequest: false,
            timeoutMillis: 10_000,
        };

        const _options = {
            ...defaultOptions,
            ...options,
        };

        const requestId = this.createRequestId();

        const { onlyRequest, callback } = _options;

        //콜백 응답을 받지 않는다면..
        if (onlyRequest) {
            //네이티브 브릿지 호출
            this.postMessage(action, requestId, payload);

            return;
        }

        const defaultCallback = {
            once: true,
            fn: () => {},
        };

        const mergedCallbackInf = {
            ...defaultCallback,
            ...callback,
        };

        console.log(
            '# NativeBridgeService callWithCallback mergedCallbackInf',
            JSON.stringify(mergedCallbackInf),
        );

        useNativeBridgeCallbackRegistryStore
            .getState()
            .registerCallback(requestId, { ...mergedCallbackInf });

        //앱(안드로이드/iOS)별 네이티브 브릿지 호출 처리
        this.postMessage(action, requestId, payload);
    }

    /**
     * 네이티브 브릿지 call with Promise Pattern
     * @param action 네이티브 브릿지 액션 문자열
     * @param payload 네이티브 브릿지 전달 데이터
     * @param options
     * @returns
     */
    public call<I extends any = {}, O extends any = {}>(
        action: string,
        payload?: I, // iOS 쿼리 파라미터 구성용
        options?: NativeBridgeCallOptions<O>,
    ): Promise<NativeBridgeResponse<O> | null> {
        if (!isApp()) {
            return Promise.reject(new Error('NOT APP'));
        }

        // if (this.pendingRequests.has(action)) {
        //     return Promise.reject(new Error(`DUPLICATE_NATIVE_CALL: ${action}`));
        // }

        const defaultOptions = {
            onlyRequest: false,
            timeoutMillis: 120_000,
        };

        const _options = {
            ...defaultOptions,
            ...options,
        };

        const cleanup = () => this.pendingRequests.delete(action);

        const requestId = this.createRequestId();

        const { onlyRequest, timeoutMillis } = _options;

        //콜백 응답을 받지 않는다면..
        if (onlyRequest) {
            //네이티브 브릿지 호출
            this.postMessage(action, requestId, payload);

            cleanup();
            return Promise.resolve(null);
        }

        const promise = new Promise<NativeBridgeResponse<O>>((resolve) => {
            let timeoutHandle: number | null = null;

            const callbackInf = {
                once: true, //
                fn: (response: NativeBridgeResponse<O>) => {
                    if (timeoutHandle !== null) {
                        clearTimeout(timeoutHandle);
                    }

                    //
                    cleanup();
                    resolve(response as NativeBridgeResponse<O>);
                },
            };

            useNativeBridgeCallbackRegistryStore
                .getState()
                .registerCallback(requestId, callbackInf);

            //timeoutMillis -1이면 타임아웃 미사용 처리
            if (timeoutMillis !== -1) {
                timeoutHandle = setTimeout(() => {
                    useNativeBridgeCallbackRegistryStore.getState().unregisterCallback(requestId);

                    const error = {
                        code: 'TIMEOUT',
                        message: `NativeBridge call timeout (${timeoutMillis})`,
                    };
                    resolve({
                        requestId: requestId,
                        status: 'fail',
                        error,
                    });
                }, timeoutMillis);
            }

            //앱(안드로이드/iOS)별 네이티브 브릿지 호출 처리
            this.postMessage(action, requestId, payload);
        }).finally(() => {
            //
            cleanup();
        });

        this.pendingRequests.set(action, promise);

        return promise;
    }

    /**
     * 네이티브 브릿지 호출
     * @param command 브릿지 커맨드
     * @param callbackId  브릿지 콜백ID
     * @param request 요청 데이터
     * @returns
     */
    private postMessage<I>(action: string, requestId: string, payload?: I): void {
        console.log(
            `# NativeBridgeService postMessage ${action}, ${requestId}, ${JSON.stringify(payload)}`,
        );

        const request = {
            action,
            requestId,
            payload,
        };

        if (this.isAndroid()) {
            console.log(
                '### android window.nativeBridge?.postMessage=',
                window.nativeBridge?.postMessage,
            );
            //안드로이드인경우
            if (window.nativeBridge?.postMessage) {
                window.nativeBridge.postMessage(JSON.stringify(request));
            }
            return;
        } else if (this.isIOS()) {
            console.log(
                '### ios window.webkit?.messageHandlers?.nativeBridge?.postMessage =',
                window.webkit?.messageHandlers?.nativeBridge?.postMessage,
            );
            //iOS인경우
            if (window.webkit?.messageHandlers?.nativeBridge?.postMessage) {
                window.webkit.messageHandlers.nativeBridge.postMessage(JSON.stringify(request));
                return;
            }

            return;
        }
    }

    /** 플랫폼이 안드로이드인지 확인 */
    private isAndroid(): boolean {
        return /Android/i.test(navigator.userAgent);
    }
    /** 플랫폼이 iOS인지 확인 */
    private isIOS(): boolean {
        return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
}

export { NativeBridgeService };
